import axios from 'axios';

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const BASE_URL = process.env.REACT_APP_REPORTS_BASE_URL;

// The reports server sits on the player's save path, so a hung connection must
// not hold the game hostage: every call gets a deadline.
const REPORT_TIMEOUT_MS = 8000;

const uuid4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Echoed request fields that look "id-ish" but are never the report's own
// id — the guest add/update payload sends these back in some response
// shapes, and picking one up as the report id would corrupt the next
// round's update call.
const NON_REPORT_ID_KEYS = new Set([
  'userid', 'organizationid', 'sessionid', 'gameid', 'employeeid', 'themeid',
]);

/** Depth-limited scan for any *id-ish* key not in NON_REPORT_ID_KEYS, used as
 * a fallback when a response uses a field name we don't already know about. */
function deepFindReportId(obj, depth) {
  if (depth > 3 || obj == null || typeof obj !== 'object') return null;
  for (const [key, val] of Object.entries(obj)) {
    const lower = key.toLowerCase();
    if (
      /(^|_)id$/.test(lower) &&
      !NON_REPORT_ID_KEYS.has(lower) &&
      (typeof val === 'string' || typeof val === 'number') &&
      String(val).trim() !== ''
    ) {
      return String(val).trim();
    }
  }
  for (const val of Object.values(obj)) {
    if (val && typeof val === 'object') {
      const nested = deepFindReportId(val, depth + 1);
      if (nested) return nested;
    }
  }
  return null;
}

/** Pull report id from game-server responses (shape varies by endpoint). */
export function extractReportIdFromResponse(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const candidates = [
    payload.id,
    payload.reportId,
    payload.report_id,
    payload.data?.id,
    payload.data?.reportId,
    payload.data?.report_id,
    payload.result?.id,
    payload.result?.reportId,
    payload.report?.id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  // None of the known field names matched — the guest endpoints in
  // particular have returned unlisted shapes before, silently disabling
  // updates for the rest of the session. Fall back to a generic scan rather
  // than giving up and leaving every later round to POST "add" again.
  return deepFindReportId(payload, 0);
}

export async function refreshToken(currentToken) {
  const url = `${BASE_URL}/auth/rf-token`;
  const headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    'Idempotency-Key': uuid4(),
  };
  const response = await axios.get(url, { headers, timeout: REPORT_TIMEOUT_MS });
  return response.data.token;
}

export async function sendReport(data) {
  const { role, token } = data;
  if (role === 'demobypass' || role === 'DEMO') return true;

  // Stable per-operation key (supplied by the caller) so a retry of the same
  // stage is recognisable to the reports server; random only as a fallback.
  const idempotencyKey = data.idempotencyKey || uuid4();
  // Transport-only field: keep it out of the request body sent to the server.
  const { idempotencyKey: _omit, ...payload } = data;

  const isGuest = role === 'GUEST_USER';
  if (isGuest) {
    const endpoint = data.reportId ? 'game/update/guest/score' : 'game/add/guest/score';
    // Update carries the same identity fields as add (not just reportId) —
    // if the guest endpoint matches the row by session identity rather than
    // solely by the opaque report id, an update missing those fields could
    // fail to match anything server-side and silently create a new row
    // instead, which is exactly the "still inserting, not updating" symptom.
    const guestPayload = {
      points: data.points,
      time: data.time,
      gameId: data.gameId,
      name: data.name,
      sessionId: data.sessionId,
      userId: data.userId,
      organizationId: data.organizationId,
      ans: '',
      ...(data.reportId && { reportId: data.reportId }),
    };
    const url = `${BASE_URL}/${endpoint}`;
    const method = data.reportId ? 'put' : 'post';
    const headers = {
      'client-secret': CLIENT_SECRET,
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    };
    const response = await axios({ method, url, headers, data: guestPayload, timeout: REPORT_TIMEOUT_MS });
    return response.data;
  }

  const isUpdate = Boolean(data.reportId);
  const url = isUpdate
    ? `${BASE_URL}/game-server/report/${data.reportId}`
    : `${BASE_URL}/game-server/report/add`;
  const method = isUpdate ? 'put' : 'post';

  let headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'Idempotency-Key': idempotencyKey,
  };

  try {
    const response = await axios({ method, url, headers, data: payload, timeout: REPORT_TIMEOUT_MS });
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      const newToken = await refreshToken(token);
      headers.Authorization = `Bearer ${newToken}`;
      const retry = await axios({ method, url, headers, data: payload, timeout: REPORT_TIMEOUT_MS });
      return retry.data;
    }
    throw err;
  }
}
